import {
  Breadcrumbs,
  Callout,
  Card,
  type BreadcrumbProps,
} from "@blueprintjs/core";
import {
  useGetMediaGridQuery,
  type GetMediaGridQuery,
} from "@package/ui-gql/index-data-access";
import { useEffect, useMemo, useState } from "react";
import { setSelectedParentIdHierarchy } from "../../../../store/slices/slice-media";
import { useAppDispatch, useAppSelector } from "../../../../store/store";

type MediaGridBreadCrumbsProps = {
  disabled?: boolean;
};

export function MediaGridBreadCrumbs({
  disabled = false,
}: MediaGridBreadCrumbsProps) {
  const [parentMedia, setParentMedia] = useState<GetMediaGridQuery["items"]>(
    [],
  );

  const dispatch = useAppDispatch();
  const selectedParentIdHierarchy = useAppSelector(
    (state) => state.media.selectedParentIdHierarchy,
  );

  const {
    data: dataMediaRoot,
    loading: loadingMediaRoot,
    error: errorMediaRoot,
  } = useGetMediaGridQuery({
    variables: {
      filter: {
        isDir: true,
        root: true,
      },
    },
  });
  const {
    data: dataMediaParents,
    loading: loadingMediaParents,
    error: errorMediaParents,
  } = useGetMediaGridQuery({
    variables: {
      filter: {
        ids: selectedParentIdHierarchy,
      },
    },
    skip: !selectedParentIdHierarchy?.length,
  });

  const rootId = dataMediaRoot?.items?.[0]?.id;
  const loading = loadingMediaParents || loadingMediaRoot;

  const breadCrums: BreadcrumbProps[] = useMemo(
    () =>
      selectedParentIdHierarchy.map<BreadcrumbProps>((id, idx) => {
        const parent = parentMedia.find((p) => p.id === id);
        const last = idx === selectedParentIdHierarchy.length - 1;

        return {
          current: last,
          disabled: last || loading || disabled,
          icon: "folder-open",
          onClick: () => {
            if (!last) {
              dispatch(
                setSelectedParentIdHierarchy(
                  selectedParentIdHierarchy.slice(0, idx + 1),
                ),
              );
            }
          },
          text: parent?.name,
        };
      }) ?? [],
    [disabled, dispatch, loading, parentMedia, selectedParentIdHierarchy],
  );

  useEffect(
    function effectOnRootDataChanged() {
      if (rootId) {
        dispatch(setSelectedParentIdHierarchy([rootId]));
      }
    },
    [dispatch, rootId],
  );

  useEffect(
    function effectOnParentMediaFetched() {
      if (!loadingMediaParents) {
        setParentMedia(dataMediaParents?.items ?? []);
      }
    },
    [dataMediaParents?.items, loadingMediaParents],
  );

  if (errorMediaParents || errorMediaRoot) {
    return (
      <Callout intent="danger">Failed loading media folder structure.</Callout>
    );
  }

  return (
    <Card compact elevation={1}>
      <Breadcrumbs items={breadCrums} />
    </Card>
  );
}
